import styled from 'styled-components'
import { css } from 'styled-components'
import { useEveryNthBlock } from '@/hooks/useBlockNumber'
import {
  useBtcLrtAllowances,
  useBtcLrtAuth,
  useBtcLrtBalances,
  useBtcLrtVault,
  useBtcLrtWithdrawRequest,
  useBtcLrtVaultStats,
  useBtcLrtVaultState,
  useBtcLrtUserPoints,
  useBtcLrtVaultPoints,
  useBtcLrtVaultCalls,
} from '@/state/yearnVault/hooks'
import { useEffect } from 'react'
import { ThemeData } from '@/swell-ui/theme/branding'
import { YearnAeraPageTemplate } from '@/components/YearnAeraPage/YearnAeraPageTemplate'
import eigenLayerGreyLogo from '@/assets/images/eigen-grey-80x80.png'
import karakLogo from '@/assets/images/karak-80x80.png'
import blackPearlLogo from '@/assets/images/black-pearl-80x80.png'
import { VaultYieldSource } from '@/types/yearnAera'
import { YearnAeraPointsCampaigns } from '@/components/YearnAeraPage/types'
import { usePrices } from '@/state/prices/hooks'

const symbioticLogo =
  'https://uploads-ssl.webflow.com/63d9b0d075e8161e9281ebdd/66693e5d00dc4d1921a9395e_Symbiotic.svg'

const READ_MORE_HREF = 'https://www.swellnetwork.io/post/swbtc'

const titleCSS = css`
  .v-title {
    color: ${ThemeData.Symbiotic.symbioticTitleGrey};
    .btc {
      color: ${ThemeData.Symbiotic.symbioticBtcOrange};
    }
    ${({ theme }) => theme.breakpoints.up('sm')} {
      white-space: nowrap;
      br {
        display: none;
      }
    }
  }
`
const title = (
  <>
    Swell <span className="btc">BTC</span> LRT
  </>
)

const pageDescription = (
  <>
    An automated vault that restakes WBTC into restaking protocols like
    Symbiotic (Karak and Eigenlayer coming soon) to generate yield and secure
    BTCFi.{' '}
    <a href={READ_MORE_HREF} target="_blank" rel="noopener noreferrer">
      Read more here
    </a>
    .
  </>
)

const TERMS_OF_SERVICE_HREF =
  'https://www.swellnetwork.io/legal/terms-of-service'
const VAULT_AGREEMENT_HREF =
  'https://www.swellnetwork.io/legal/gauntlet-vault-agreement-and-disclaimer'

const vaultDescription = (
  <>
    <p>
      This is a multi-strategy vault that optimally restakes wBTC into a mix of
      BTC restaking protocols for maximum risk adjusted yields.
    </p>
    <br />
    <p>
      Disclaimer:
      <br />
      swBTC vault is not open to persons or citizens of the United States and
      other restricted countries - for more details please refer to the{' '}
      <a href={TERMS_OF_SERVICE_HREF} rel="noopener noreferrer" target="_blank">
        terms of service
      </a>{' '}
      and{' '}
      <a href={VAULT_AGREEMENT_HREF} rel="noopener noreferrer" target="_blank">
        vault agreement
      </a>
      .
    </p>
  </>
)
const pointsCampaigns: YearnAeraPointsCampaigns = {
  blackPearls: {
    logoURI: blackPearlLogo,
    name: 'Swell Black Pearls',
    multiplier: 3,
    comingSoon: false,
  },
  symbiotic: {
    logoURI: symbioticLogo,
    name: 'Symbiotic Points',
    multiplier: 1,
    comingSoon: false,
  },
  eigen: {
    logoURI: eigenLayerGreyLogo,
    name: 'Eigenlayer Points',
    multiplier: 1,
    comingSoon: true,
  },
  karak: {
    logoURI: karakLogo,
    name: 'Karak Points',
    multiplier: 1,
    comingSoon: true,
  },
}

const yieldSources: VaultYieldSource[] = [
  {
    name: 'Symbiotic',
    logoURI: symbioticLogo,
    comingSoon: false,
  },
  {
    name: 'Eigenlayer',
    logoURI: eigenLayerGreyLogo,
    comingSoon: true,
  },
  {
    name: 'Karak',
    logoURI: karakLogo,
    comingSoon: true,
  },
]

export function BtcLrtVault() {
  const vault = useBtcLrtVault()

  const vaultStatsQuery = useBtcLrtVaultStats()
  const vaultStateQuery = useBtcLrtVaultState()
  const authQuery = useBtcLrtAuth()
  const balancesQuery = useBtcLrtBalances()
  const allowancesQuery = useBtcLrtAllowances()
  const withdrawRequestQuery = useBtcLrtWithdrawRequest()
  const userPointsQuery = useBtcLrtUserPoints()
  const vaultPointsQuery = useBtcLrtVaultPoints()
  const pricesQuery = usePrices()

  useEveryNthBlock({
    blocksPerFetch: 1,
    fetch: () => {
      vaultStateQuery.mutate()
      allowancesQuery.mutate()
      balancesQuery.mutate()
      withdrawRequestQuery.mutate()
    },
    skip: false,
  })

  useEveryNthBlock({
    blocksPerFetch: 4,
    fetch: () => {
      vaultStatsQuery.mutate()
      pricesQuery.mutate()
    },
    skip: false,
  })

  useEveryNthBlock({
    blocksPerFetch: 10,
    fetch: () => {
      userPointsQuery.mutate()
      vaultPointsQuery.mutate()
    },
    skip: false,
  })

  const calls = useBtcLrtVaultCalls()
  const {
    approveAssetForDeposit,
    approveVaultTokenForWithdraw,
    cancelWithdraw,
    deposit,
    requestWithdraw,
    completeWithdraw,
  } = calls

  useEffect(() => {
    if (
      approveAssetForDeposit.status === approveAssetForDeposit.STATUS.FULFILLED
    ) {
      allowancesQuery.mutate()
    }
  }, [
    allowancesQuery,
    approveAssetForDeposit.STATUS.FULFILLED,
    approveAssetForDeposit.status,
  ])
  useEffect(() => {
    if (
      approveVaultTokenForWithdraw.status ===
      approveVaultTokenForWithdraw.STATUS.FULFILLED
    ) {
      allowancesQuery.mutate()
    }
  }, [
    allowancesQuery,
    approveVaultTokenForWithdraw.STATUS.FULFILLED,
    approveVaultTokenForWithdraw.status,
  ])
  useEffect(() => {
    if (deposit.status === deposit.STATUS.FULFILLED) {
      balancesQuery.mutate()
    }
  }, [balancesQuery, deposit.STATUS.FULFILLED, deposit.status])
  useEffect(() => {
    if (requestWithdraw.status === requestWithdraw.STATUS.FULFILLED) {
      withdrawRequestQuery.mutate()
      balancesQuery.mutate()
    }
  }, [balancesQuery, requestWithdraw, withdrawRequestQuery])
  useEffect(() => {
    if (cancelWithdraw.status === cancelWithdraw.STATUS.FULFILLED) {
      withdrawRequestQuery.mutate()
      balancesQuery.mutate()
    }
  }, [balancesQuery, cancelWithdraw, withdrawRequestQuery])
  useEffect(() => {
    if (completeWithdraw.status === completeWithdraw.STATUS.FULFILLED) {
      balancesQuery.mutate()
      withdrawRequestQuery.mutate()
    }
  }, [
    balancesQuery,
    completeWithdraw,
    completeWithdraw.status,
    withdrawRequestQuery,
  ])

  const allowances = allowancesQuery.data
  const balances = balancesQuery.data
  const depositAssetUsdRate = pricesQuery.data?.btcUsd
  const withdrawRequest = withdrawRequestQuery.data
  const vaultStats = vaultStatsQuery.data
  const auth = authQuery.data
  const userPoints = userPointsQuery.data
  const vaultPoints = vaultPointsQuery.data
  const vaultState = vaultStateQuery.data

  return (
    <PageStyles>
      <YearnAeraPageTemplate
        vault={vault}
        title={title}
        pageDescription={pageDescription}
        vaultDescription={vaultDescription}
        yieldSources={yieldSources}
        pointsCampaigns={pointsCampaigns}
        userPoints={userPoints}
        depositAssetUsdRate={depositAssetUsdRate}
        vaultStats={vaultStats}
        allowances={allowances}
        auth={auth}
        balances={balances}
        calls={calls}
        vaultPoints={vaultPoints}
        vaultState={vaultState}
        withdrawRequest={withdrawRequest}
      />
    </PageStyles>
  )
}

const PageStyles = styled.div`
  ${titleCSS}
`
